import { TableCopyMethod } from '@/analytics/enums/table.enums'
import { useEffect, useState } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { MenuContent } from '@/components/menu/MenuContent'
import { Check, Copy, Edit } from '@/assets/icons'

const actionStyle =
  'flex items-center gap-1 whitespace-nowrap px-2 py-1 text-xs font-medium leading-4 text-brand-neutral-800 hover:bg-brand-neutral-100 hover:text-brand-neutral-950 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500 aria-disabled:opacity-50'

/** The same cell actions and feedback for hover, single selection, and ranges. */
export default function TableCopyPill({
  count = 1,
  onEdit,
  onCopy,
  allowFootnotes = false,
  onMenuOpenChange,
  defaultMenuOpen = false,
}: {
  defaultMenuOpen?: boolean
  onMenuOpenChange?: (open: boolean) => void
  allowFootnotes?: boolean
  count?: number
  onEdit?: () => void
  onCopy?: (footnotes: boolean, method?: TableCopyMethod) => Promise<void>
}) {
  const [menuOpen, setMenuOpen] = useState(defaultMenuOpen)
  const setOpen = (open: boolean) => {
    setMenuOpen(open)
    onMenuOpenChange?.(open)
  }
  const mac = /Mac|iPhone|iPad/.test(navigator.platform)
  const [copied, setCopied] = useState<boolean | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  useEffect(() => {
    if (copied === null) return
    const timeout = window.setTimeout(() => setCopied(null), 1500)
    return () => window.clearTimeout(timeout)
  }, [copied])
  const copy = async (footnotes: boolean, method = TableCopyMethod.Action) => {
    if (!onCopy || busy) return
    setBusy(true)
    setCopied(null)
    setError('')
    try {
      await onCopy(footnotes, method)
      setCopied(footnotes)
    } catch {
      setError('Unable to copy. Please try again.')
    } finally {
      setBusy(false)
    }
  }
  const confirmation = `Copied ${count} ${count === 1 ? 'cell' : 'cells'}`
  return (
    <div
      role="toolbar"
      aria-label={count > 1 ? 'Selected cells' : 'Cell actions'}
      onKeyDownCapture={(event) => {
        if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'c') {
          event.preventDefault()
          event.stopPropagation()
          setOpen(false)
          void copy(event.shiftKey && allowFootnotes, TableCopyMethod.Shortcut)
        }
      }}
    >
      <span role="status" className="sr-only">
        {copied !== null ? confirmation : ''}
      </span>
      <div className="flex items-center divide-x divide-brand-neutral-300">
        {onEdit && count === 1 && (
          <button type="button" className={actionStyle} onClick={onEdit}>
            Edit <Edit aria-hidden className="h-3.5 w-3.5" />
          </button>
        )}
        {onCopy &&
          (allowFootnotes ? (
            <DropdownMenu.Root modal={false} open={menuOpen} onOpenChange={setOpen}>
              <DropdownMenu.Trigger asChild>
                <button type="button" className={actionStyle} disabled={busy} aria-busy={busy}>
                  {copied !== null ? confirmation : 'Copy'}
                  {copied !== null ? <Check aria-hidden className="h-3.5 w-3.5" /> : <Copy aria-hidden className="h-3.5 w-3.5" />}
                </button>
              </DropdownMenu.Trigger>
              <MenuContent align="end" side="bottom" className="p-1">
                {[false, true].map((footnotes) => (
                  <DropdownMenu.Item
                    key={String(footnotes)}
                    className={`${actionStyle} w-full cursor-default justify-between gap-6 rounded outline-none data-[highlighted]:bg-brand-neutral-100`}
                    aria-keyshortcuts={`${mac ? 'Meta' : 'Control'}+${footnotes ? 'Shift+' : ''}C`}
                    onSelect={() => void copy(footnotes)}
                  >
                    <span>{footnotes ? 'Copy with footnotes' : 'Copy'}</span>
                    <kbd
                      aria-hidden
                      className="rounded border border-brand-neutral-300 bg-brand-neutral-100 px-1 font-sans text-[11px] font-normal text-brand-neutral-700"
                    >
                      {mac ? (footnotes ? '⌘⇧C' : '⌘C') : footnotes ? 'Ctrl+Shift+C' : 'Ctrl+C'}
                    </kbd>
                  </DropdownMenu.Item>
                ))}
              </MenuContent>
            </DropdownMenu.Root>
          ) : (
            <button type="button" className={actionStyle} aria-disabled={busy} aria-busy={busy} onClick={() => void copy(false)}>
              {copied !== null ? confirmation : 'Copy'}
              {copied !== null ? <Check aria-hidden className="h-3.5 w-3.5" /> : <Copy aria-hidden className="h-3.5 w-3.5" />}
            </button>
          ))}
      </div>
      {error && (
        <p role="alert" className="px-2 py-1 text-xs text-brand-neutral-800">
          {error}
        </p>
      )}
    </div>
  )
}
